import { type ComponentProps, forwardRef, memo, type ReactNode, useMemo } from 'react';

import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import { WORKSPACE_AGENT_PANEL_MENTION_ROW_THUMBNAIL } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import { useWorkspaceAgentSelectAssetStore } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetStoreContext';

export type WorkspaceAgentSelectAssetTriggerProps = Omit<ComponentProps<'button'>, 'value'> & {
  'data-testid'?: string;
};

export const WorkspaceAgentSelectAssetTrigger = memo(
  forwardRef<HTMLButtonElement, WorkspaceAgentSelectAssetTriggerProps>(function WorkspaceAgentSelectAssetTrigger(
    { 'data-testid': testId = 'WORKSPACE_AGENT_SELECT_ASSET_TRIGGER', ...rest },
    ref,
  ) {
    const isOpen = useWorkspaceAgentSelectAssetStore((state) => state.isOpen);
    const value = useWorkspaceAgentSelectAssetStore((state) => state.value);

    const content = useMemo((): ReactNode => {
      if (!value) {
        return <span className="min-w-0 flex-1 truncate text-gray-9">Select asset</span>;
      }

      let thumbnail: ReactNode = null;

      if (value.thumbnailUrl) {
        thumbnail = (
          <WorkspaceAgentPanelImage
            {...WORKSPACE_AGENT_PANEL_MENTION_ROW_THUMBNAIL}
            alt=""
            className="size-full object-cover"
            src={value.thumbnailUrl}
          />
        );
      }

      return (
        <>
          <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-4 bg-gray-3">
            {thumbnail}
          </span>
          <span className="min-w-0 flex-1 truncate" title={value.title}>
            {value.title}
          </span>
        </>
      );
    }, [value]);

    return (
      <button
        type="button"
        {...rest}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className={cn(
          'flex h-8 w-full min-w-0 items-center gap-2 rounded-8 border border-gray-5 bg-gray-1 px-2 text-left text-12 text-gray-12 hover:border-gray-6',
          value ? 'pr-9' : undefined,
        )}
        data-testid={testId}
        ref={ref}
      >
        {content}
      </button>
    );
  }),
);

WorkspaceAgentSelectAssetTrigger.displayName = 'WorkspaceAgentSelectAssetTrigger';
